import CryptoJS from 'crypto-js';
import { GenericType, Permission, Role, TextVariable, User } from './types';
import axios, { AxiosRequestConfig } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { SelectOption } from './components/Select';

/*---------------------------------------------------------
# Encryption Functions
---------------------------------------------------------*/

const secretPass = process.env.REACT_APP_SECRETPASS ?? '';

export const encryptKey = (key: string) => {
    const encryptedKey = CryptoJS.AES.encrypt(key, secretPass).toString();

    return encryptedKey;
};

export const decryptKey = (key: string) => {
    const bytes = CryptoJS.AES.decrypt(key, secretPass);
    const decryptedKey = bytes.toString(CryptoJS.enc.Utf8);

    return decryptedKey;
};

/*---------------------------------------------------------
# Auth Functions
---------------------------------------------------------*/

export const saveSessionCredentials = (user: User) => {
    const sessionUser = prepareUserObject(user);

    const encryptedUser = encryptKey(JSON.stringify(sessionUser));
    localStorage.setItem('user', encryptedUser);
};

export const getSessionCredentials = (): User | null => {
    const session = localStorage.getItem('user');

    if (session) {
        const decryptedUser = decryptKey(localStorage.getItem('user') ?? '');
        const user = JSON.parse(decryptedUser) as User;

        return user;
    } else {
        return null;
    }
};


// --- utils.ts ---
export async function compressFileToBase64(
  file: File,
  maxKB: number = 200,
  maxWidth: number = 1024
): Promise<string> {
  // carrega como URL
  const url = URL.createObjectURL(file);
  const img = await new Promise<HTMLImageElement>(resolve => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.src = url;
  });

  // dimensiona canvas
  const ratio = img.width / img.height;
  const width = Math.min(img.width, maxWidth);
  const height = width / ratio;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, width, height);

  // qualidade inicial e itera até baixar abaixo de maxKB
  let quality = 0.9;
  let blob: Blob | null = null;
  do {
    blob = await new Promise<Blob | null>(res =>
      canvas.toBlob(b => res(b), 'image/jpeg', quality)
    );
    if (!blob) break;
    const sizeKB = blob.size / 1024;
    if (sizeKB <= maxKB) break;
    quality -= 0.1;     // reduz qualidade aos poucos
  } while (quality > 0.1);

  URL.revokeObjectURL(url);
  // converte para base64
  return new Promise<string>(res => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result as string);
    reader.readAsDataURL(blob!);
  });
}


export const clearSessionCredentials = (navigate: NavigateFunction) => {
    localStorage.clear();
    console.clear();
    navigate('/auth/sign-in');
};

export const verifyRoutePermissions = (
    needs: string[],
    navigate: NavigateFunction
): User | boolean => {
    const user = getSessionCredentials();
    let canAccess = true;

    needs.forEach(need => {
        switch (need) {
            case 'auth':
                if (!user) {
                    canAccess = false;
                    clearSessionCredentials(navigate);
                }
                break;

            case 'not.auth':
                if (user) {
                    canAccess = false;
                    navigate('/');
                }
                break;

            case 'email.verified':
                if (user && !user.email_verified_at) {
                    canAccess = false;
                    navigate('/auth/email-verification');
                }
                break;

            case 'not.email.verified':
                if (user && user.email_verified_at) {
                    canAccess = false;
                    navigate('/');
                }
                break;

            case 'pending.update':
                if (
                    user &&
                    !(
                        user.gender === null ||
                        (user?.image === null && sysCanSaveImages()) ||
                        !user?.lgpd_agreed
                    )
                ) {
                    canAccess = false;
                    navigate('/');
                }
                break;

            case 'not.pending.update':
                if (
                    user &&
                    (user.gender === null ||
                        user.gender === undefined ||
                        ((user?.image === null || user?.image === undefined) &&
                            sysCanSaveImages()) ||
                        !user?.lgpd_agreed ||
                        user?.lgpd_agreed === undefined)
                ) {
                    canAccess = false;
                    navigate('/auth/user/pending-update');
                }
                break;

            case 'logicmanager.admin':
                if (user && !user.roles.find(role => ['ADMIN'].includes(role.internal_type))) {
                    canAccess = false;
                    navigate('/');
                }
                break;

            case 'logicmanager.classes':
                if (
                    user &&
                    !user.roles.find(role => ['ADMIN', 'TEACHER'].includes(role.internal_type))
                ) {
                    canAccess = false;
                    navigate('/');
                }
                break;

            case 'logicmanager.reports':
                if (
                    user &&
                    !user.roles.find(role => ['ADMIN', 'TEACHER'].includes(role.internal_type))
                ) {
                    canAccess = false;
                    navigate('/');
                }
                break;
        }
    });

    if (true) {
        return user as User;
    } else {
        return false;
    }
};

export const logout = async (token: string, navigate: NavigateFunction) => {
    try {
        const requestConfig: AxiosRequestConfig = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
            }
        };

        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, [], requestConfig);

        clearSessionCredentials(navigate);
    } catch (error) {
        const response = {
            message: 'Erro ao fazer logout',
            error: error
        };

        console.error(response);

        return response as any;
    }
};

/*---------------------------------------------------------
# User Functions
---------------------------------------------------------*/

export const prepareUserObject = (user: any): User => {
    // Set user model (view interface)
    const userModel = {
        id: 0,
        image: {} ,
        name: '',
        surname: '',
        gender: '',
        email: '',
        email_verified_at: null,
        lgpd_agreed: false,
        roles: [] as Role[],
        token: ''
    } as User;

    // Filter coming user properties
    Object.keys(user).forEach(prop => {
        let found = false;

        Object.keys(userModel).forEach(propModel => {
            if (propModel === prop) {
                found = true;
            }
        });

        if (!found) {
            delete user[prop];
        }
    });

    if (user.image && user.image.status) {
        // Decorate user image
        user.image = {
            id: user.image.id,
            url: user.image.url,
            status_internal_type: user.image.status.internal_type,
            status_name: user.image.status.name
        } 
    }

    // Decorate user roles
    user.roles = user.roles.map((role: any) => {
        return {
            id: role.id,
            internal_type: role.internal_type,
            name: role.name,
            is_academic: role.is_academic == 1
        } as Role;
    });

    return user as User;
};

export const rolesToString = (roles: Role[], separator: string, prop: string = 'name') => {
    let string = '';

    for (const role of roles) {
        if (!role.is_academic) {
            string =
                string === '' ? string.concat(role[prop]) : string.concat(separator, role[prop]);
        }
    }

    return string;
};

export const hasPermission = (permission: Permission, user: User) => {
    let hasPermission = false;

    switch (permission) {
        case 'logicmanager.admin':
            hasPermission =
                undefined !==
                user.roles.find(role => {
                    return ['ADMIN'].includes(role.internal_type);
                });
            break;

        case 'logicmanager.classes':
            hasPermission =
                undefined !==
                user.roles.find(role => {
                    return ['ADMIN', 'TEACHER'].includes(role.internal_type);
                });
            break;

        case 'trilogic.admin':
            hasPermission =
                undefined !==
                user.roles.find(role => {
                    return ['ADMIN', 'TEACHER'].includes(role.internal_type);
                });
            break;
    }

    return hasPermission;
};

export const hasRoles = (roles: string[], user: User) => {
    return (
        undefined !==
        user.roles.find(role => {
            return roles.includes(role.internal_type);
        })
    );
};

/*---------------------------------------------------------
# Feedback Functions
---------------------------------------------------------*/

export const getPronoun = (gender: string) => {
    switch (gender) {
        case 'M':
            return 'o';

        case 'F':
            return 'a';

        case 'N':
            return 'e';

        default:
            return 'o(a)';
    }
};

export const handleTextVariables = (text: string, textVariables: TextVariable[]) => {
    for (const textVariable of textVariables) {
        text = text.replaceAll(textVariable.variable, textVariable.value);
    }

    return text;
};

/*---------------------------------------------------------
# Error Handler
---------------------------------------------------------*/

export const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
        case 'ER001':
            return 'Usuário não encontrado';

        case 'ER002':
            return 'Credenciais inválidas';

        case 'ER040':
            return 'O usuário foi desabilitado. Solicite a ativação deste usuário através de nosso e-mail <trilogic.canoas.ifrs.edu.br>.';

        case 'ER041':
            return 'Erro ao buscar cursos';

        case 'ER042':
            return 'Erro ao criar curso';

        case 'ER043':
            return 'Erro ao atualizar curso';

        case 'ER044':
            return 'Erro ao excluir curso';

        case 'ER045':
            return 'O curso não pode ser excluído pois está vinculado a uma ou mais turmas';

        default:
            return 'Um erro inesperado aconteceu. Relate este problema através de nosso e-mail <trilogic@canoas.ifrs.edu.br>.';
    }
};

/*---------------------------------------------------------
# Generic Functions
---------------------------------------------------------*/

export const getRandomNumber = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const isArrayEmpty = (array: any[]) => {
    let isEmpty = true;

    array.forEach(item => {
        if (item !== undefined) {
            isEmpty = false;
        }
    });

    return isEmpty;
};

export const capitalize = (input: string) => {
    return input
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const getYearOptions = (year: string | number) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const inputYear =
        year !== '' ? (typeof year === 'string' ? parseInt(year) : year) : currentYear;
    const startYear = (inputYear < currentYear ? inputYear : currentYear) - 2;
    const endYear = inputYear > currentYear + 2 ? inputYear : currentYear + 2;

    const years: SelectOption[] = [];

    for (let i = startYear; i <= endYear; i++) {
        years.push({
            key: `year-${i}`,
            value: `${i}`,
            text: `${i}`
        });
    }

    return years;
};

export const getHighestAcademicUserPosition = (academicUsers: any[]) => {
    let higherPosition = 0;

    academicUsers.forEach(academicUser => {
        if (academicUser.position > higherPosition) {
            higherPosition = academicUser.position;
        }
    });

    return higherPosition;
};

export const getReportFilterTypes = (isAdmin: boolean) => {
    const filterTypes: SelectOption[] = [];

    filterTypes.push({
        key: 'empty',
        value: '',
        text: 'Selecione um tipo de filtro'
    });

    if (isAdmin) {
        filterTypes.push({
            key: 'users',
            value: 'users',
            text: 'Filtro de usuários'
        });
    }

    filterTypes.push({
        key: 'classes',
        value: 'classes',
        text: 'Filtro de turmas'
    });

    filterTypes.push({
        key: 'schools',
        value: 'schools',
        text: 'Filtro de instituições'
    });

    return filterTypes;
};

export const sysCanSaveImages = () => {
    return true;
};

export const maxImageUpload = () => {
    return 1024 * 1024 * 10;
};

 /**
 * giftToExercises.js
 *
 * Função completa que:
 *  - faz um parse básico/robusto de GIFT (extrai name, question, rawAnswer, type, answers)
 *  - converte o resultado para o formato de "exercises" que você especificou
 *
 * Export: giftToExercises(content: string) => { exercises: [...], diagnostics: [...] }
 *
 * Observação: este parser tenta cobrir a maioria dos casos do arquivo de exemplo GIFT.
 */

/* ============================
   PARSER GIFT (simples + robusto)
   ============================ */
function parseGIFT(content) {
    if (typeof content !== 'string') throw new Error('parseGIFT espera uma string');
  
    // normaliza quebras
    content = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
    // remove linhas de comentário que começam com // (após espaços/tabs)
    content = content.replace(/^[ \t]*\/\/.*$/mg, '');
  
    // reduz múltiplas linhas vazias
    content = content.replace(/\n{3,}/g, '\n\n');
  
    const questions: any = [];
    let i = 0;
    const len = content.length;
  
    function skipWhitespace() {
      while (i < len && /\s/.test(content[i])) i++;
    }
  
    // util: encontra próximo índice de substring sem considerar escapes (apenas para simples buscas)
    function indexOfFrom(str, needle, from) {
      return content.indexOf(needle, from);
    }
  
    while (i < len) {
      skipWhitespace();
      if (i >= len) break;
  
      // detectar ::Name:: prefix opcional
      let name = null;
      if (content.slice(i, i + 2) === '::') {
        const endNameIdx = content.indexOf('::', i + 2);
        if (endNameIdx !== -1) {
          name = content.slice(i + 2, endNameIdx).trim();
          i = endNameIdx + 2;
        }
      }
  
      skipWhitespace();
      if (i >= len) break;
  
      // procurar próximo '{' que inicia o bloco de resposta
      const braceOpenIdx = content.indexOf('{', i);
      if (braceOpenIdx === -1) {
        // sem mais blocos, podemos tentar pegar texto restante como pergunta solta e sair
        const rest = content.slice(i).trim();
        if (rest) {
          questions.push({ name, question: rest, rawAnswer: '', type: null, answers: [] });
        }
        break;
      }
  
      // question text = do cursor até braceOpenIdx
      let questionText = content.slice(i, braceOpenIdx).trim();
  
      // se questionText começar com ::Name:: (caso não capturado antes)
      if (!name && questionText.startsWith('::')) {
        const match = questionText.match("/^::([^:]*)::(.*)$/s");
        if (match) {
          name = match[1].trim();
          questionText = match[2].trim();
        }
      }
  
      // extrair bloco de chaves balanceadas
      let depth = 0;
      let j = braceOpenIdx;
      let closed = false;
      for (; j < len; j++) {
        const ch = content[j];
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) {
            closed = true;
            break;
          }
        }
      }
      if (!closed) {
        // pega até o fim
        j = len - 1;
      }
  
      const rawAnswer = content.slice(braceOpenIdx + 1, j).trim();
  
      // tokeniza answers do bloco
      const answers = tokenizeAnswerBlock(rawAnswer);
  
      const qtype = guessTypeFromBlock(rawAnswer, answers);
  
      questions.push({
        name,
        question: questionText,
        rawAnswer,
        type: qtype,
        answers
      });
  
      i = j + 1;
    }
  
    return questions;
  }
  
  /* tokenização do bloco de respostas */
  function tokenizeAnswerBlock(raw) {
    if (!raw || !raw.length) return [];
  
    const s = raw;
  
    // numérico: começa com '#'
    if (s.trim().startsWith('#')) {
      return [{ marker: '#', text: s.trim(), feedback: null, weight: null, pair: null }];
    }
  
    const tokens : any = [];
    let k = 0;
    const L = s.length;
  
    while (k < L) {
      // procurar próximo marcador '=' ou '~'
      while (k < L && !(/[~=]/).test(s[k])) k++;
      if (k >= L) break;
      const marker = s[k];
      k++;
      const start = k;
      while (k < L && !(/[~=]/).test(s[k])) k++;
      let tokenText = s.slice(start, k).trim();
  
      // extrair feedback (#), weight %...% e matching -> se existirem
      let feedback: any = null;
      let weight : any = null;
      let pair: any = null;
  
      const hashIdx = findUnescaped(tokenText, '#');
      if (hashIdx !== -1) {
        feedback = tokenText.slice(hashIdx + 1).trim();
        tokenText = tokenText.slice(0, hashIdx).trim();
      }
  
      const weightMatch = tokenText.match(/^%(-?\d+(?:\.\d+)?)%/);
      if (weightMatch) {
        weight = parseFloat(weightMatch[1]);
        tokenText = tokenText.slice(weightMatch[0].length).trim();
      }
  
      const arrowIdx = findUnescaped(tokenText, '->');
      if (arrowIdx !== -1) {
        const left = tokenText.slice(0, arrowIdx).trim();
        const right = tokenText.slice(arrowIdx + 2).trim();
        pair = { left, right };
      }
  
      tokens.push({
        marker,
        text: tokenText,
        feedback,
        weight,
        pair
      });
    }
  
    // fallback: se não achou tokens, tentar dividir por linhas que começam com =/~ ou por novas linhas
    if (tokens.length === 0) {
      const lines = s.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      for (const l of lines) {
        const m = l.match("/^([=~])(.*)$/s");
        if (m) {
          const marker = m[1];
          let text = m[2].trim();
          let feedback = null;
          const hi = findUnescaped(text, '#');
          if (hi !== -1) {
            feedback = text.slice(hi + 1).trim();
            text = text.slice(0, hi).trim();
          }
          tokens.push({ marker, text, feedback, weight: null, pair: (text.includes('->') ? (()=>{ const [l,r]=text.split('->'); return {left:l.trim(), right:r.trim()}; })() : null) });
        }
      }
    }
  
    return tokens;
  }
  
  /* heurística de tipo */
  function guessTypeFromBlock(raw, answers) {
    const t = (raw || '').trim();
    if (!t) return null;
    if (t.startsWith('#')) return 'numerical';
    const upper = t.toUpperCase();
    if (upper === 'T' || upper === 'F' || upper === 'TRUE' || upper === 'FALSE') return 'truefalse';
    if (answers.some(a => a.pair)) return 'matching';
    if (answers.length && answers.every(a => a.marker === '=')) {
      if (answers.length === 1) return 'shortanswer';
      return 'shortanswer/multiple';
    }
    return 'multichoice';
  }
  
  /* util: encontra substring não escapada */
  function findUnescaped(str, needle) {
    if (!str) return -1;
    let pos = str.indexOf(needle);
    while (pos !== -1) {
      let bs = 0, p = pos - 1;
      while (p >= 0 && str[p] === '\\') { bs++; p--; }
      if (bs % 2 === 0) return pos;
      pos = str.indexOf(needle, pos + 1);
    }
    return -1;
  }
  
  /* ============================
     CONVERSOR PARA EXERCISES
     ============================ */
  
  export function giftToExercises(content) {
    const parsed = parseGIFT(content);
    return robustConvert(parsed);
  }


  
  
  function robustConvert(parsedQuestions) {
    if (!Array.isArray(parsedQuestions)) throw new Error('Esperado array vindo do parseGIFT');
  
    const templateDropAreas = [
      {
        id: 1, name: 'lixeira', type: 'trash', pos: { x: 0.37, y: 0.3 }, dropAreas: [], scale: 1,
        text: 'Texto', imagem: 'https://i.gifer.com/origin/34/34338d26023e5515f6cc8969aa027bca.gif',
        borderStyle: 'none',
        css: { borderStyle: 'none', color: '#000', borderColor: 'black', transform: 'scale(1)' },
        width: 0.1189358372456964, height:0.15966386554621848
      },
      {
        id: 2, name: 'dropbox', type: 'dropBox', pos: { x: 0.15, y: 0.25 }, bgColor: '#fff', scale: 1,
        text: 'Coloque aqui a alternativa correta', multiple: true,
        css: {
          borderWidth: '3.3px', borderStyle: 'solid', borderColor: '#1eff00', backgroundColor: '#dfffdb',
          borderRadius: '6.5%', color: '#000000', fontFamily: 'Tahoma, Geneva, sans-serif'
        },
        width: 0.1903367496339678, height: 0.1902587519025875, dropAreas: [],
        imagem: 'https://i.gifer.com/origin/34/34338d26023e5515f6cc8969aa027bca.gif', borderStyle: 'none'
      }
    ];
  
    /* util: encontra substring não escapada */
    function findUnescapedLocal(str, needle) {
      if (!str) return -1;
      let pos = str.indexOf(needle);
      while (pos !== -1) {
        let bs = 0, p = pos - 1;
        while (p >= 0 && str[p] === '\\') { bs++; p--; }
        if (bs % 2 === 0) return pos;
        pos = str.indexOf(needle, pos + 1);
      }
      return -1;
    }
  
    /* limpa e normaliza o texto da pergunta */
    function cleanQuestionText(raw) {
      if (!raw) return '';
      let s = String(raw).trim();
      s = s.replace(/^::[^:]*::/, '').trim(); // remove ::name:: prefix
      // remove bloco {...} terminal se houver
      s = s.replace(/\{[\s\S]*\}$/, '').trim();
      s = s.replace(/^\s*\d+[\.\)]\s*/, ''); // remove "1. " ou "1) "
      s = s.replace(/\s+/g, ' ').trim();
      return s;
    }
  
    /* extrai primeiro bloco {...} balanceado */
    function extractFirstBraceBlock(str) {
      if (!str) return null;
      const start = str.indexOf('{');
      if (start === -1) return null;
      let depth = 0;
      for (let i = start; i < str.length; i++) {
        const ch = str[i];
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) {
            return str.slice(start + 1, i); // conteúdo
          }
        }
      }
      return null;
    }
  
    /* split top-level em tokens que começam com =, ~ ou # */
    function splitBlockToParts(block) {
      if (!block) return [];
      const parts : any = [];
      const re = /([=~#])([\s\S]*?)(?=(?:[=~#])|$)/g;
      let m;
      while ((m = re.exec(block)) !== null) {
        parts.push({ marker: m[1], content: m[2].trim() });
      }
      if (parts.length === 0) {
        const lines = block.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        return lines.map(l => ({ marker: null, content: l }));
      }
      return parts;
    }
  
    /* limpa um fragmento de alternativa (remove peso %...% e feedback #...) */
    function normalizeOptionText(raw) {
      if (raw == null) return '';
      let t = String(raw).trim();
      t = t.replace(/^[=~#]\s*/, '');
      t = t.replace(/^%(-?\d+(?:\.\d+)?)%/, '').trim();
      const hi = findUnescapedLocal(t, '#');
      if (hi !== -1) t = t.slice(0, hi).trim();
      t = t.replace(/\s+/g, ' ').trim();
      return t;
    }
  
    function isCorrectFromToken(token, marker) {
      if (!token && !marker) return false;
      if (token && token.marker === '=') return true;
      if (token && token.marker === '#') return true;
      if (marker === '=') return true;
      if (token && typeof token.weight === 'number' && Math.abs(token.weight - 100) < 1e-9) return true;
      return false;
    }
  
    const exercises : any = [];
  
    for (let qi = 0; qi < parsedQuestions.length; qi++) {
      const q = parsedQuestions[qi] || {};
      const rawCombined = [q.question, q.name, q.rawAnswer].filter(Boolean).join('\n\n');
  
      // obter description
      let description = cleanQuestionText(q.question || '');
      let sourceUsedForDescription = 'question';
      if (!description) {
        description = cleanQuestionText(q.name || '');
        sourceUsedForDescription = description ? 'name' : sourceUsedForDescription;
      }
      if (!description) {
        const idx = rawCombined.indexOf('{');
        if (idx !== -1) {
          const before = rawCombined.slice(0, idx).trim();
          if (before.length > 2) {
            description = cleanQuestionText(before);
            sourceUsedForDescription = '{-before-block-}';
          }
        }
      }
      if (!description) {
        const lines = rawCombined.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const candidate = lines.find(l => l.length > 10);
        if (candidate) {
          description = cleanQuestionText(candidate);
          sourceUsedForDescription = 'first-long-line';
        }
      }
  
      // obter steps
      const steps : any = [];
      const tokens = Array.isArray(q.answers) ? q.answers : [];
  
      let block = extractFirstBraceBlock(q.rawAnswer) || extractFirstBraceBlock(q.question) || extractFirstBraceBlock(q.name) || extractFirstBraceBlock(rawCombined);
      if (!block && (tokens.length === 0) && q.rawAnswer && q.rawAnswer.trim()) {
        block = q.rawAnswer.trim();
      }
  
      let partsFromBlock = [];
      if (block) partsFromBlock = splitBlockToParts(block);
  
      const maxLen = Math.max(tokens.length, partsFromBlock.length);
  
      if (maxLen === 0) {
        const fallbackText = (q.rawAnswer && q.rawAnswer.trim()) || '(sem alternativas detectadas)';
        steps.push({
          id: 1,
          text: fallbackText,
          originalMarker: null,
          weight: null,
          feedback: null,
          pair: null,
          dropAreas: ['lixeira'],
          imagem: null,
          css: {},
          correct: false
        });
      } else {
        for (let i = 0; i < maxLen; i++) {
          const token = tokens[i];
          const part: any = partsFromBlock[i];
  
          let text = '';
          let marker = token && token.marker ? token.marker : (part && part.marker ? part.marker : null);
          let feedback = token && token.feedback ? token.feedback : null;
          let weight = token && (typeof token.weight === 'number') ? token.weight : null;
          let pair = token && token.pair ? token.pair : null;
  
          if (token && token.text && String(token.text).trim().length) {
            text = String(token.text).trim();
          } else if (pair) {
            text = `${pair.left} -> ${pair.right}`;
          } else if (part && part.content) {
            text = normalizeOptionText(part.content);
            if (!feedback) {
              const hi = findUnescapedLocal(part.content, '#');
              if (hi !== -1) feedback = part.content.slice(hi + 1).trim();
            }
            if (weight === null) {
              const wt = (part.content || '').match(/^%(-?\d+(?:\.\d+)?)%/);
              if (wt) weight = parseFloat(wt[1]);
            }
            if (!pair && part.content && part.content.includes('->')) {
              const [l, r] = part.content.split('->');
              pair = { left: (l||'').trim(), right: (r||'').trim() };
              if (!text || text === '') text = `${pair.left} -> ${pair.right}`;
            }
          } else {
            continue
          }
  
          const correct = isCorrectFromToken(token, marker);
  
          steps.push({
            id: (i+2) + 1,
            text,
            originalMarker: marker,
            weight,
            feedback,
            pair,
            dropAreas: correct ? ['dropbox'] : ['lixeira'],
            imagem: null,
            css: {backgroundColor: "rgba(84,186,94,1)"},
            correct: !!correct
          });
        }
      }
  
      let title = (q.name && String(q.name).trim()) ? String(q.name).trim() : '';
      if (!title) {
        title = description ? description.split(/\r?\n/)[0].slice(0, 120) : `Questão ${qi + 1}`;
      }
  
      exercises.push({
        title,
        description: description || '(pergunta não detectada)',
        json: { dropAreas: templateDropAreas, steps }
      });
    }
  
    return exercises;
  }
  